import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  downloadReport,
  generateReport,
  listReports,
  type Report,
} from "@/api/reports";
import { useToast } from "@/contexts/ToastContext";
import { formatDate } from "@/lib/utils";

const TIPOS = ["lecturas", "hits", "operativos", "telemetria", "kpis", "auditoria"] as const;
const FORMATOS = ["pdf", "xlsx", "csv"] as const;

export function ReportsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [tipo, setTipo] = useState<(typeof TIPOS)[number]>("lecturas");
  const [formato, setFormato] = useState<(typeof FORMATOS)[number]>("xlsx");
  const { data } = useQuery({
    queryKey: ["reports"],
    queryFn: listReports,
    refetchInterval: 5_000,
  });

  const generate = useMutation({
    mutationFn: () => generateReport({ tipo, formato }),
    onSuccess: () => {
      toast.push("Reporte solicitado", "success");
      qc.invalidateQueries({ queryKey: ["reports"] });
    },
    onError: () => toast.push("No se pudo generar", "error"),
  });

  return (
    <div className="p-6 space-y-4" data-testid="page-reports">
      <header>
        <h1 className="text-2xl font-semibold">Reportes</h1>
      </header>
      <div className="card flex flex-wrap items-end gap-3">
        <div>
          <div className="text-xs text-muted-200 mb-1">Tipo</div>
          <select
            data-testid="report-tipo"
            className="input"
            value={tipo}
            onChange={(e) => setTipo(e.target.value as (typeof TIPOS)[number])}
          >
            {TIPOS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div>
          <div className="text-xs text-muted-200 mb-1">Formato</div>
          <select
            data-testid="report-formato"
            className="input"
            value={formato}
            onChange={(e) => setFormato(e.target.value as (typeof FORMATOS)[number])}
          >
            {FORMATOS.map((f) => (
              <option key={f} value={f}>
                {f.toUpperCase()}
              </option>
            ))}
          </select>
        </div>
        <button
          data-testid="report-generate"
          className="btn-primary"
          onClick={() => generate.mutate()}
        >
          Generar
        </button>
      </div>

      <div className="card">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-200">
            <tr className="border-b border-border">
              <th className="py-2">Tipo</th>
              <th>Formato</th>
              <th>Estado</th>
              <th>Solicitado</th>
              <th>Terminado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(data?.items ?? []).map((r: Report) => (
              <tr key={r.id} className="border-b border-border/40" data-testid={`report-row-${r.id}`}>
                <td className="py-2">{r.tipo}</td>
                <td>{r.formato.toUpperCase()}</td>
                <td>
                  <span
                    className={
                      "rounded px-2 py-0.5 text-xs " +
                      (r.estado === "listo"
                        ? "bg-success/20 text-success"
                        : r.estado === "error"
                        ? "bg-danger/20 text-danger"
                        : "bg-warning/20 text-warning")
                    }
                  >
                    {r.estado}
                  </span>
                </td>
                <td>{formatDate(r.solicitadoAt)}</td>
                <td>{formatDate(r.terminadoAt)}</td>
                <td>
                  {r.estado === "listo" && (
                    <button
                      className="btn-ghost"
                      data-testid={`report-download-${r.id}`}
                      onClick={async () => {
                        try {
                          const dl = await downloadReport(r.id);
                          window.open(dl.url, "_blank", "noopener");
                        } catch {
                          toast.push("Error al descargar", "error");
                        }
                      }}
                    >
                      Descargar
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
